import React, { useEffect, useState } from 'react';
import { Save, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: '', new_password: '' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then((res) => setSettings(res.data)).catch(() => toast.error('Failed to load settings'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    try {
      await api.post('/admin/change-password', pw);
      toast.success('Password changed');
      setPw({ current_password: '', new_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password change failed');
    } finally {
      setPwSaving(false);
    }
  };

  const inputCls = 'w-full h-11 rounded-lg bg-white border border-[#E6E8EE] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#26247B]';

  if (!settings) {
    return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#26247B]" /></div>;
  }

  return (
    <div data-testid="admin-settings" className="max-w-2xl">
      <h1 className="text-2xl font-extrabold text-[#26247B]">Settings</h1>
      <p className="mt-1 text-sm text-black/60">Site-wide configuration — reCAPTCHA keys, notification email and SEO meta.</p>

      <div className="mt-6 bg-white border border-[#E6E8EE] rounded-2xl p-6 space-y-5">
        <h2 className="font-extrabold text-[#26247B]">Anti-Spam (Google reCAPTCHA v3)</h2>
        <p className="text-xs text-black/60 leading-5 bg-[#FFF7E5] border border-[#F4A300]/40 rounded-lg px-4 py-3">
          reCAPTCHA v3 runs invisibly (no checkbox) and scores each submission for risk. Keys are domain-locked —
          make sure this domain is registered at{' '}
          <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noreferrer" className="underline font-bold">google.com/recaptcha/admin</a>{' '}
          for verification to succeed on this site.
        </p>
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-black/70 uppercase">reCAPTCHA Site Key (public)</label>
          <input data-testid="admin-settings-recaptcha-site-input" value={settings.recaptcha_site_key || ''} onChange={(e) => setSettings({ ...settings, recaptcha_site_key: e.target.value })} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-black/70 uppercase">reCAPTCHA Secret Key</label>
          <input data-testid="admin-settings-recaptcha-secret-input" value={settings.recaptcha_secret_key || ''} onChange={(e) => setSettings({ ...settings, recaptcha_secret_key: e.target.value })} className={inputCls} />
        </div>

        <h2 className="font-extrabold text-[#26247B] pt-2">Notifications</h2>
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-black/70 uppercase">Form notification email</label>
          <input value={settings.notify_email || ''} onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} className={inputCls} />
          <p className="text-xs text-black/50">Form submissions are stored in the CMS inbox. On the production (Reseller Club) server, submissions are also emailed to this address via PHP mail.</p>
        </div>

        <h2 className="font-extrabold text-[#26247B] pt-2">SEO</h2>
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-black/70 uppercase">Site Title</label>
          <input value={settings.site_title || ''} onChange={(e) => setSettings({ ...settings, site_title: e.target.value })} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-black/70 uppercase">Site Description</label>
          <textarea value={settings.site_description || ''} onChange={(e) => setSettings({ ...settings, site_description: e.target.value })} rows={3} className="w-full rounded-lg bg-white border border-[#E6E8EE] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#26247B]" />
        </div>

        <button
          onClick={save}
          data-testid="admin-settings-save-button"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-[#2E8B57] text-white px-6 py-2.5 text-sm font-bold transition-colors duration-200 hover:bg-[#257247] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings
        </button>
      </div>

      <div className="mt-6 bg-white border border-[#E6E8EE] rounded-2xl p-6 space-y-5">
        <h2 className="font-extrabold text-[#26247B] flex items-center gap-2"><KeyRound className="h-5 w-5" /> Change Admin Password</h2>
        <form onSubmit={changePassword} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-black/70 uppercase">Current Password</label>
            <input type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-black/70 uppercase">New Password (min 8 characters)</label>
            <input type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} />
          </div>
          <button
            type="submit"
            disabled={pwSaving}
            className="inline-flex items-center gap-2 rounded-lg bg-[#26247B] text-white px-6 py-2.5 text-sm font-bold transition-colors duration-200 hover:bg-[#1f1d66] disabled:opacity-60"
          >
            {pwSaving && <Loader2 className="h-4 w-4 animate-spin" />} Change Password
          </button>
        </form>
      </div>
    </div>
  );
}
